import React from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { HelpCircle, Target, Sparkles, Upload, TrendingDown, CheckCircle2, Bot } from 'lucide-react';

const STEPS = [
  {
    icon: Target,
    title: 'Pick a year & scope',
    body: 'Choose the target year, then decide whether the goal is for your whole portfolio (org-wide) or a single property.',
  },
  {
    icon: TrendingDown,
    title: 'Choose a target type',
    body: 'Set an absolute CO₂e goal (e.g. "≤ 120 t for 2026") or a % reduction versus a baseline year (e.g. "−10% vs 2025 actual").',
  },
  {
    icon: Sparkles,
    title: 'Or let us recommend one',
    body: 'Tap "Recommend" to get two data-backed options from last year\'s actual: a steady −5%/yr goal and a science-based −4.2%/yr goal (1.5°C aligned).',
  },
  {
    icon: Upload,
    title: 'Bulk-upload property targets',
    body: 'Download the CSV template, fill in a target per property (site_uid), and upload it to set many goals at once.',
  },
  {
    icon: CheckCircle2,
    title: 'Track actual vs target',
    body: 'The gauge shows how this year\'s actual emissions track against your goal — green when on track, red when over.',
  },
  {
    icon: Bot,
    title: 'Ask Astrid',
    body: 'You can also ask Astrid in chat to recommend targets, set them up for you, or export a full sustainability report (PDF / Excel / CSV).',
  },
];

export default function TargetsHelpDrawer() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-900"
          data-testid="targets-help-trigger"
        >
          <HelpCircle className="h-4 w-4" /> How targets work
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[440px] overflow-y-auto"
        data-testid="targets-help-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-emerald-700">
            <Target className="h-5 w-5" /> Setting up emissions targets
          </SheetTitle>
          <SheetDescription>
            A quick guide to defining CO₂e goals and tracking your progress.
          </SheetDescription>
        </SheetHeader>

        <ol className="mt-6 space-y-5">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3" data-testid={`targets-help-step-${i}`}>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <s.icon className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <span className="font-mono text-xs text-emerald-600">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </div>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 rounded-md border border-emerald-100 bg-emerald-50/60 p-3 text-[11px] leading-relaxed text-emerald-900">
          <b>Tip:</b> emissions are estimated with US EPA factors (eGRID 2022 electricity,
          EPA GHG Hub natural gas, embedded-energy for water). Lower is better — a target is
          "on track" while actual emissions stay at or below your goal.
        </div>
      </SheetContent>
    </Sheet>
  );
}
